import React, { useState } from 'react';
import { workSessionApi, auditsApi } from '../services/api';
import { Clock, User, RefreshCw, Printer, ChevronDown, ChevronRight, AlertTriangle, Shield, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface EmployeeData {
  usuario: {
    id: number;
    name: string;
    role: string;
    email: string;
  };
  estadoLogin: {
    estaLogueado: boolean;
    tiempoLogueado: number;
    ultimoLogin: string | null;
    necesitaAutorizacion: boolean;
    autorizacionSupervisor: boolean;
    horasOnline: number;
  };
  estadisticas: {
    totalHorasLogueadas: number;
    horasTrabajadasAuditorias: number;
    eficienciaProductiva: number;
    diasTrabajados: number;
    auditoriasCompletadas: number;
    correcciones: number;
    totalComisiones: number;
    promedioHorasDiarias: number;
    porcentajeCumplimiento: number;
  };
  auditoriaActual: {
    id: number;
    titulo: string;
    cliente: string;
    estado: string;
    fechaInicio: string;
    horasEnProceso: number;
  } | null;
  infoSueldo: {
    id: number;
    tipoPago: string;
    sueldoReferencia: number;
    sueldoFinal: number;
    porcentajeObjetivo: number;
    pagoFrancos: number;
    cantidadFrancos: number;
    estado: string;
    fechaIngreso: string;
  } | null;
  auditoriasPorCliente: any;
  alertasTiempo: any[];
  detallesDias?: {
    [fecha: string]: {
      horasTrabajadas: number;
      auditoriasCompletadas: number;
      ganado: number;
      sesionActiva: boolean;
    };
  };
}

interface DayDetail {
  fecha: string;
  sesion: {
    loginTime: string;
    logoutTime: string | null;
    horasTrabajadas: number;
  } | null;
  resumen: {
    totalHoras: number;
    totalAuditorias: number;
    totalGanado: number;
  };
  auditorias: Array<{
    id: number;
    cliente: string;
    sucursal: string;
    estado: string;
    tiempoTrabajado: number;
    tiempoEstimado: number;
    indicador: 'ok' | 'lento' | 'rapido';
    comision: number;
  }>;
}

const PersonalTracking: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [empleados, setEmpleados] = useState<EmployeeData[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expandedEmployees, setExpandedEmployees] = useState<Set<number>>(new Set());
  const [filterRole, setFilterRole] = useState<'todos' | 'operador' | 'supervisor'>('todos');
  const [filterStatus, setFilterStatus] = useState<'todos' | 'logueados' | 'con_auditoria'>('todos');
  const [selectedDay, setSelectedDay] = useState<{ userId: number; fecha: string } | null>(null);
  const [dayDetail, setDayDetail] = useState<DayDetail | null>(null);
  const [loadingDay, setLoadingDay] = useState(false);
  const [auditoriasOperador, setAuditoriasOperador] = useState<Record<number, any[]>>({});
  const [loadingAuditorias, setLoadingAuditorias] = useState<Record<number, boolean>>({});
  const [showReassignModal, setShowReassignModal] = useState<{ auditId: number; titulo: string; operadorActualId: number; operadorActual: string } | null>(null);
  const [reassignOperadorId, setReassignOperadorId] = useState('');
  const [reassignMensaje, setReassignMensaje] = useState('');

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatHours = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const formatHoursFromHours = (hours: number) => {
    const h = Math.floor(hours);
    const mins = Math.round((hours - h) * 60);
    return mins > 0 ? `${h}h ${mins}m` : `${h}h`;
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await workSessionApi.getEmployeeSummary(selectedMonth, selectedYear, searchTerm);
      if (response.data.success) {
        const filteredEmpleados = response.data.data.empleados
          .filter((emp: EmployeeData) => emp.usuario.role !== 'admin' && emp.usuario.role !== 'cliente')
          .sort((a: EmployeeData, b: EmployeeData) => {
            // Primero logueados
            if (a.estadoLogin.estaLogueado && !b.estadoLogin.estaLogueado) return -1;
            if (!a.estadoLogin.estaLogueado && b.estadoLogin.estaLogueado) return 1;
            // Luego por alertas
            if (a.alertasTiempo.length > b.alertasTiempo.length) return -1;
            if (a.alertasTiempo.length < b.alertasTiempo.length) return 1;
            // Finalmente alfabéticamente
            return a.usuario.name.localeCompare(b.usuario.name, 'es');
          });

        setEmpleados(filteredEmpleados);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setEmpleados([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSession = async (userId: number, userName: string) => {
    if (!window.confirm(`¿Cerrar sesión de ${userName}?`)) return;

    try {
      await workSessionApi.closeUserSession(userId);
      await loadData(); // Recargar datos
    } catch (error) {
      console.error('Error cerrando sesión:', error);
      alert('Error al cerrar sesión');
    }
  };

  const handleAuthorizeOvertime = async (userId: number, userName: string) => {
    const motivo = prompt(`Autorizar horas extras para ${userName}.\n\nMotivo:`);
    if (!motivo) return;

    try {
      await workSessionApi.authorizeOvertime(userId, { motivo, duracionHoras: 4 });
      await loadData();
    } catch (error) {
      console.error('Error autorizando horas extras:', error);
      alert('Error al autorizar horas extras');
    }
  };

  const toggleEmployee = (userId: number) => {
    setExpandedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
        cargarAuditoriasOperador(userId);
      }
      return newSet;
    });
  };

  const loadDayDetail = async (userId: number, fecha: string) => {
    setLoadingDay(true);
    setSelectedDay({ userId, fecha });
    try {
      const response = await workSessionApi.getDayDetail(userId, fecha);
      if (response.data.success) {
        setDayDetail(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando detalle del día:', error);
      alert('Error al cargar el detalle del día');
    } finally {
      setLoadingDay(false);
    }
  };

  const cargarAuditoriasOperador = async (userId: number) => {
    setLoadingAuditorias(prev => ({ ...prev, [userId]: true }));
    try {
      const response = await auditsApi.getAll({ operadorId: userId, all: true });
      const todas = response.data?.data?.auditorias || response.data?.data || response.data || [];
      const activas = (Array.isArray(todas) ? todas : []).filter(
        (a: any) => ['asignada', 'en_proceso', 'pausada'].includes(a.estado)
      );
      setAuditoriasOperador(prev => ({ ...prev, [userId]: activas }));
    } catch (error) {
      console.error('Error cargando auditorías del operador:', error);
      setAuditoriasOperador(prev => ({ ...prev, [userId]: [] }));
    } finally {
      setLoadingAuditorias(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleReassign = async () => {
    if (!showReassignModal || !reassignOperadorId) return;
    try {
      await auditsApi.reassignAudit(showReassignModal.auditId, parseInt(reassignOperadorId), reassignMensaje || undefined);
      // Recargar auditorías de ambos operadores
      await cargarAuditoriasOperador(showReassignModal.operadorActualId);
      const destinoId = parseInt(reassignOperadorId);
      if (expandedEmployees.has(destinoId)) {
        await cargarAuditoriasOperador(destinoId);
      }
      setShowReassignModal(null);
      setReassignOperadorId('');
      setReassignMensaje('');
    } catch (error: any) {
      console.error('Error reasignando auditoría:', error);
      alert(error.response?.data?.message || 'Error al reasignar auditoría');
    }
  };

  const handleLiberar = async (auditId: number, operadorId: number) => {
    if (!window.confirm('¿Liberar esta auditoría? Se desasignará del operador.')) return;
    try {
      await auditsApi.reassignAudit(auditId, undefined, undefined);
      await cargarAuditoriasOperador(operadorId);
    } catch (error: any) {
      console.error('Error liberando auditoría:', error);
      alert(error.response?.data?.message || 'Error al liberar auditoría');
    }
  };

  const handleEliminar = async (auditId: number, titulo: string, operadorId: number) => {
    if (!window.confirm(`¿Eliminar la auditoría "${titulo}"? Esta acción no se puede deshacer.`)) return;
    try {
      await auditsApi.deleteAudit(auditId, 'cleanup');
      await cargarAuditoriasOperador(operadorId);
    } catch (error: any) {
      console.error('Error eliminando auditoría:', error);
      alert(error.response?.data?.message || 'Error al eliminar auditoría');
    }
  };

  const getEstadoBadge = (estado: string): { bg: string; color: string; label: string } => {
    switch (estado) {
      case 'asignada': return { bg: '#dbeafe', color: '#1e40af', label: 'Asignada' };
      case 'en_proceso': return { bg: '#dcfce7', color: '#15803d', label: 'En proceso' };
      case 'pausada': return { bg: '#fef3c7', color: '#92400e', label: 'Pausada' };
      default: return { bg: '#f3f4f6', color: '#374151', label: estado };
    }
  };

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month, 0).getDate();
  };

  const generarInformeImprimible = (empleado: EmployeeData) => {
    const monthName = new Date(selectedYear, selectedMonth - 1).toLocaleDateString('es', { month: 'long', year: 'numeric' });
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Informe - ${empleado.usuario.name} - ${monthName}</title>
        <style>
          @media print {
            @page { margin: 1cm; }
            body { margin: 0; }
          }
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          h1 {
            color: #1f2937;
            border-bottom: 3px solid #3b82f6;
            padding-bottom: 10px;
            margin-bottom: 20px;
          }
          h2 {
            color: #374151;
            margin-top: 25px;
            margin-bottom: 15px;
            font-size: 1.2em;
          }
          .header-info {
            background: #f3f4f6;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 25px;
          }
          .header-info div {
            margin: 5px 0;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 15px;
            margin-bottom: 25px;
          }
          .summary-card {
            background: #eff6ff;
            padding: 15px;
            border-radius: 8px;
            text-align: center;
          }
          .summary-card .label {
            color: #6b7280;
            font-size: 0.85em;
            margin-bottom: 5px;
          }
          .summary-card .value {
            color: #1f2937;
            font-size: 1.5em;
            font-weight: bold;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 15px;
            page-break-inside: avoid;
          }
          th {
            background: #3b82f6;
            color: white;
            padding: 12px;
            text-align: left;
            font-weight: 600;
          }
          td {
            padding: 10px 12px;
            border-bottom: 1px solid #e5e7eb;
          }
          tr:nth-child(even) {
            background: #f9fafb;
          }
          .total-row {
            background: #dbeafe !important;
            font-weight: bold;
            border-top: 2px solid #3b82f6;
          }
          .no-data {
            color: #9ca3af;
            font-style: italic;
            text-align: center;
            padding: 20px;
          }
          .print-button {
            background: #3b82f6;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 1em;
            margin: 20px auto;
            display: block;
          }
          .print-button:hover {
            background: #2563eb;
          }
          @media print {
            .print-button { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>📊 Informe Detallado de Trabajo</h1>

        <div class="header-info">
          <div><strong>Empleado:</strong> ${empleado.usuario.name}</div>
          <div><strong>Rol:</strong> ${empleado.usuario.role === 'operador' ? 'Operador' : empleado.usuario.role === 'supervisor' ? 'Supervisor' : empleado.usuario.role}</div>
          <div><strong>Período:</strong> ${monthName}</div>
          <div><strong>Fecha de generación:</strong> ${new Date().toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
        </div>

        <h2>📈 Resumen del Período</h2>
        <div class="summary-grid">
          <div class="summary-card">
            <div class="label">Total Auditorías</div>
            <div class="value">${empleado.estadisticas.auditoriasCompletadas}</div>
          </div>
          ${empleado.usuario.role === 'supervisor' ? `
          <div class="summary-card">
            <div class="label">Correcciones</div>
            <div class="value">${empleado.estadisticas.correcciones || 0}</div>
          </div>
          ` : ''}
          <div class="summary-card">
            <div class="label">Horas Logueadas</div>
            <div class="value">${formatHoursFromHours(empleado.estadisticas.totalHorasLogueadas)}</div>
          </div>
          <div class="summary-card">
            <div class="label">Total Ganado</div>
            <div class="value">${formatMoney(empleado.estadisticas.totalComisiones)}</div>
          </div>
        </div>

        ${empleado.infoSueldo && empleado.infoSueldo.pagoFrancos > 0 ? `
        <div style="background: #fef3c7; padding: 12px; border-radius: 6px; margin-bottom: 20px; border-left: 4px solid #f59e0b;">
          <strong>💼 Incluye Francos:</strong> ${formatMoney(empleado.infoSueldo.pagoFrancos)}
          ${empleado.infoSueldo.cantidadFrancos ? ` (${empleado.infoSueldo.cantidadFrancos} días)` : ''}
        </div>
        ` : ''}

        <h2>📅 Detalle Diario</h2>
    `;

    if (empleado.detallesDias && Object.keys(empleado.detallesDias).length > 0) {
      html += `
        <table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Cliente</th>
              <th>Auditoría</th>
              <th>Horas</th>
              <th>Ganado</th>
            </tr>
          </thead>
          <tbody>
      `;

      let totalAuditorias = 0;
      let totalHoras = 0;
      let totalGanado = 0;

      // Ordenar fechas
      const fechasOrdenadas = Object.keys(empleado.detallesDias).sort();

      fechasOrdenadas.forEach(fecha => {
        const detalle = empleado.detallesDias![fecha];
        totalAuditorias += detalle.auditoriasCompletadas;
        totalHoras += detalle.horasTrabajadas;
        totalGanado += detalle.ganado;

        const fechaFormateada = new Date(fecha + 'T12:00:00').toLocaleDateString('es-AR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });

        // Mostrar cada auditoría del día
        if (detalle.auditorias && detalle.auditorias.length > 0) {
          detalle.auditorias.forEach((audit: any, index: number) => {
            html += `
              <tr>
                <td>${index === 0 ? fechaFormateada : ''}</td>
                <td>${audit.cliente}</td>
                <td style="max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${audit.titulo}</td>
                <td>${formatHours(audit.tiempoMinutos)}</td>
                <td>${formatMoney(audit.comision)}</td>
              </tr>
            `;
          });
        } else {
          // Fallback si no hay array de auditorías
          html += `
            <tr>
              <td>${fechaFormateada}</td>
              <td colspan="2">${detalle.auditoriasCompletadas} auditoría(s)</td>
              <td>${formatHours(detalle.horasTrabajadas)}</td>
              <td>${formatMoney(detalle.ganado)}</td>
            </tr>
          `;
        }
      });

      html += `
            <tr class="total-row">
              <td><strong>TOTAL</strong></td>
              <td colspan="2"><strong>${totalAuditorias} auditorías${empleado.infoSueldo && empleado.infoSueldo.pagoFrancos > 0 ? ' + francos' : ''}</strong></td>
              <td><strong>${formatHours(totalHoras)}</strong></td>
              <td><strong>${formatMoney(totalGanado)}</strong></td>
            </tr>
          </tbody>
        </table>
      `;

      // Agregar nota al final si hay francos
      if (empleado.infoSueldo && empleado.infoSueldo.pagoFrancos > 0) {
        html += `
          <div style="background: #fef3c7; padding: 12px; border-radius: 6px; margin-top: 15px; border-left: 4px solid #f59e0b;">
            <strong>📝 Nota:</strong> El total de ${formatMoney(totalGanado)} incluye:
            <ul style="margin: 8px 0 0 20px; padding: 0;">
              <li>Comisiones por auditorías: ${formatMoney(empleado.estadisticas.totalComisiones - (empleado.infoSueldo.pagoFrancos || 0))}</li>
              <li>Pago por francos (${empleado.infoSueldo.cantidadFrancos || 0} día(s)): ${formatMoney(empleado.infoSueldo.pagoFrancos)}</li>
            </ul>
          </div>
        `;
      }
    } else {
      html += '<div class="no-data">No hay datos disponibles para este período</div>';
    }

    html += `
        <button class="print-button" onclick="window.print()">🖨️ Imprimir Informe</button>
      </body>
      </html>
    `;

    // Abrir en nueva ventana
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
    }
  };

  const getFirstDayOfMonth = (month: number, year: number) => {
    return new Date(year, month - 1, 1).getDay();
  };

  const filteredEmpleados = empleados.filter(emp => {
    // Filtro por rol
    if (filterRole !== 'todos' && emp.usuario.role !== filterRole) return false;
    // Filtro por estado
    if (filterStatus === 'logueados' && !emp.estadoLogin.estaLogueado) return false;
    if (filterStatus === 'con_auditoria' && !emp.auditoriaActual) return false;
    return true;
  });

  // No cargar automáticamente, solo cuando se presiona el botón
  React.useEffect(() => {
    if (empleados.length === 0) {
      loadData();
    }
  }, [selectedMonth, selectedYear]);

  if (!['admin', 'contable'].includes(currentUser?.role || '')) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No tienes permisos para acceder a esta sección.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem' }}>
          👥 Personal - Centro de Control
        </h1>
        <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
          Gestión y seguimiento del personal en tiempo real
        </p>
      </div>

      {/* Controles */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}>
        {/* Mes y Año */}
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          style={{
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem'
          }}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
            <option key={month} value={month}>
              {new Date(2024, month - 1).toLocaleDateString('es', { month: 'long' })}
            </option>
          ))}
        </select>

        <input
          type="number"
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          min="2020"
          max="9999"
          style={{
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            width: '100px'
          }}
        />

        {/* Filtros */}
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value as any)}
          style={{
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem'
          }}
        >
          <option value="todos">Todos los roles</option>
          <option value="operador">Operadores</option>
          <option value="supervisor">Supervisores</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as any)}
          style={{
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem'
          }}
        >
          <option value="todos">Todos</option>
          <option value="logueados">Solo logueados</option>
          <option value="con_auditoria">Con auditoría en curso</option>
        </select>

        {/* Buscador */}
        <input
          type="text"
          placeholder="Buscar empleado..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: '0.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            flex: 1,
            minWidth: '200px'
          }}
        />

        {/* Botón Actualizar */}
        <button
          onClick={loadData}
          disabled={loading}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Actualizando...' : 'Actualizar'}
        </button>
      </div>

      {/* Lista de Empleados */}
      {loading && empleados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          <RefreshCw size={32} className="animate-spin" style={{ margin: '0 auto 1rem' }} />
          <p>Cargando personal...</p>
        </div>
      ) : filteredEmpleados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
          <p>No se encontraron empleados</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredEmpleados.map((empleado) => (
            <div
              key={empleado.usuario.id}
              style={{
                backgroundColor: 'white',
                border: '2px solid',
                borderColor: empleado.estadoLogin.estaLogueado ? '#10b981' : '#e5e7eb',
                borderRadius: '0.5rem',
                overflow: 'hidden'
              }}
            >
              {/* Header del empleado */}
              <div
                style={{
                  padding: '1rem',
                  backgroundColor: empleado.estadoLogin.estaLogueado ? '#ecfdf5' : '#f9fafb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => toggleEmployee(empleado.usuario.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                  {/* Indicador de estado */}
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: empleado.estadoLogin.estaLogueado ? '#10b981' : '#9ca3af'
                  }} />

                  {/* Nombre y rol */}
                  <div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
                      {empleado.usuario.name}
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                      {empleado.usuario.role === 'operador' ? '🎯 Operador' : '👑 Supervisor'}
                    </p>
                  </div>

                  {/* Auditoría actual */}
                  {empleado.auditoriaActual && (
                    <div style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#dbeafe',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem'
                    }}>
                      📋 {empleado.auditoriaActual.cliente} ({formatHours(empleado.auditoriaActual.horasEnProceso * 60)})
                    </div>
                  )}

                  {/* Alertas */}
                  {empleado.alertasTiempo.length > 0 && (
                    <div style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#fee2e2',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      color: '#dc2626'
                    }}>
                      ⚠️ {empleado.alertasTiempo.length} alerta{empleado.alertasTiempo.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>

                {/* Resumen del mes */}
                <div style={{
                  display: 'flex',
                  gap: '2rem',
                  alignItems: 'center',
                  fontSize: '0.875rem'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>
                      {Math.round(empleado.estadisticas.totalHorasLogueadas)}hs
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>trabajadas</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>
                      {empleado.estadisticas.auditoriasCompletadas}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>auditorías</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: '#059669' }}>
                      {formatMoney(empleado.infoSueldo?.sueldoFinal || empleado.estadisticas.totalComisiones)}
                    </div>
                    <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>ganados</div>
                    {empleado.infoSueldo?.sueldoReferencia > 0 && (
                      <div style={{ fontSize: '0.625rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        {Math.round((empleado.infoSueldo.sueldoFinal / empleado.infoSueldo.sueldoReferencia) * 100)}% de ${formatMoney(empleado.infoSueldo.sueldoReferencia).replace('$', '').trim()}
                      </div>
                    )}
                  </div>

                  {/* Acciones */}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {empleado.estadoLogin.estaLogueado && (
                      <>
                        {empleado.estadoLogin.necesitaAutorizacion && !empleado.estadoLogin.autorizacionSupervisor && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAuthorizeOvertime(empleado.usuario.id, empleado.usuario.name);
                            }}
                            style={{
                              padding: '0.5rem',
                              backgroundColor: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.375rem',
                              cursor: 'pointer'
                            }}
                            title="Autorizar horas extras"
                          >
                            <Shield size={16} />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloseSession(empleado.usuario.id, empleado.usuario.name);
                          }}
                          style={{
                            padding: '0.5rem',
                            backgroundColor: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            cursor: 'pointer'
                          }}
                          title="Cerrar sesión"
                        >
                          <X size={16} />
                        </button>
                      </>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        generarInformeImprimible(empleado);
                      }}
                      style={{
                        padding: '0.5rem',
                        backgroundColor: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer'
                      }}
                      title="Imprimir reporte detallado"
                    >
                      <Printer size={16} />
                    </button>
                  </div>

                  {/* Chevron */}
                  {expandedEmployees.has(empleado.usuario.id) ? (
                    <ChevronDown size={20} />
                  ) : (
                    <ChevronRight size={20} />
                  )}
                </div>
              </div>

              {/* Detalle expandido - Calendario + Auditorías */}
              {expandedEmployees.has(empleado.usuario.id) && (
                <div style={{ padding: '1.5rem', backgroundColor: '#fafafa' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

                    {/* IZQUIERDA: Calendario existente */}
                    <div>
                      <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: '#1f2937' }}>
                        📅 Calendario del mes - {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('es', { month: 'long', year: 'numeric' })}
                      </h4>

                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '0.5rem',
                        marginBottom: '1rem'
                      }}>
                        {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
                          <div key={day} style={{
                            textAlign: 'center',
                            fontWeight: '600',
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            padding: '0.5rem'
                          }}>
                            {day}
                          </div>
                        ))}

                        {Array.from({ length: getFirstDayOfMonth(selectedMonth, selectedYear) }).map((_, i) => (
                          <div key={`empty-${i}`} />
                        ))}

                        {Array.from({ length: getDaysInMonth(selectedMonth, selectedYear) }).map((_, i) => {
                          const day = i + 1;
                          const fecha = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          const today = new Date();
                          const isToday = today.getDate() === day && today.getMonth() === selectedMonth - 1 && today.getFullYear() === selectedYear;
                          const isLoggedToday = empleado.estadoLogin.estaLogueado && isToday;

                          return (
                            <div
                              key={day}
                              onClick={() => loadDayDetail(empleado.usuario.id, fecha)}
                              style={{
                                padding: '0.75rem',
                                textAlign: 'center',
                                backgroundColor: isToday ? '#dbeafe' : 'white',
                                border: isLoggedToday ? '2px solid #10b981' : '1px solid #e5e7eb',
                                borderRadius: '0.375rem',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = isToday ? '#dbeafe' : 'white';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                            >
                              <div style={{ fontWeight: isToday ? '600' : '400' }}>{day}</div>
                              {isLoggedToday && (
                                <div style={{ fontSize: '0.625rem', color: '#10b981', marginTop: '0.25rem' }}>
                                  🟢
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* DERECHA: Panel Auditorías Activas */}
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                          📋 Auditorías activas ({auditoriasOperador[empleado.usuario.id]?.length || 0})
                        </h4>
                        <button
                          onClick={() => cargarAuditoriasOperador(empleado.usuario.id)}
                          disabled={loadingAuditorias[empleado.usuario.id]}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                        >
                          <RefreshCw size={12} className={loadingAuditorias[empleado.usuario.id] ? 'animate-spin' : ''} />
                          Recargar
                        </button>
                      </div>

                      {loadingAuditorias[empleado.usuario.id] ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                          <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 0.5rem' }} />
                          <p style={{ fontSize: '0.875rem' }}>Cargando auditorías...</p>
                        </div>
                      ) : !auditoriasOperador[empleado.usuario.id] || auditoriasOperador[empleado.usuario.id].length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af', backgroundColor: 'white', borderRadius: '0.375rem', border: '1px solid #e5e7eb' }}>
                          <p style={{ fontSize: '0.875rem' }}>Sin auditorías activas</p>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxHeight: '400px', overflowY: 'auto' }}>
                          {auditoriasOperador[empleado.usuario.id].map((audit: any) => {
                            const badge = getEstadoBadge(audit.estado);
                            return (
                              <div
                                key={audit.id}
                                style={{
                                  padding: '0.75rem',
                                  backgroundColor: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.8rem'
                                }}
                              >
                                <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                                  #{audit.id} {audit.titulo || 'Sin título'}
                                </div>
                                <div style={{ color: '#6b7280', fontSize: '0.75rem', marginBottom: '0.375rem' }}>
                                  {audit.cliente?.nombre || audit.cliente || '—'} {audit.sucursal ? `- ${audit.sucursal}` : ''}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                  <span style={{
                                    padding: '0.125rem 0.5rem',
                                    backgroundColor: badge.bg,
                                    color: badge.color,
                                    borderRadius: '1rem',
                                    fontSize: '0.7rem',
                                    fontWeight: '500'
                                  }}>
                                    {badge.label}
                                  </span>
                                  {audit.costoTotal != null && (
                                    <span style={{ fontSize: '0.75rem', color: '#059669', fontWeight: '500' }}>
                                      {formatMoney(audit.costoTotal)}
                                    </span>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                                  {['asignada', 'en_proceso', 'pausada'].includes(audit.estado) && (
                                    <button
                                      onClick={() => {
                                        setShowReassignModal({
                                          auditId: audit.id,
                                          titulo: audit.titulo || `#${audit.id}`,
                                          operadorActualId: empleado.usuario.id,
                                          operadorActual: empleado.usuario.name
                                        });
                                        setReassignOperadorId('');
                                        setReassignMensaje('');
                                      }}
                                      style={{
                                        padding: '0.2rem 0.5rem',
                                        backgroundColor: '#eff6ff',
                                        color: '#1e40af',
                                        border: '1px solid #bfdbfe',
                                        borderRadius: '0.25rem',
                                        cursor: 'pointer',
                                        fontSize: '0.7rem'
                                      }}
                                    >
                                      🔄 Reasignar
                                    </button>
                                  )}
                                  {['asignada', 'en_proceso', 'pausada'].includes(audit.estado) && (
                                    <button
                                      onClick={() => handleLiberar(audit.id, empleado.usuario.id)}
                                      style={{
                                        padding: '0.2rem 0.5rem',
                                        backgroundColor: '#fefce8',
                                        color: '#92400e',
                                        border: '1px solid #fde68a',
                                        borderRadius: '0.25rem',
                                        cursor: 'pointer',
                                        fontSize: '0.7rem'
                                      }}
                                    >
                                      🔓 Liberar
                                    </button>
                                  )}
                                  {currentUser?.role === 'admin' && (
                                    <button
                                      onClick={() => handleEliminar(audit.id, audit.titulo || `#${audit.id}`, empleado.usuario.id)}
                                      style={{
                                        padding: '0.2rem 0.5rem',
                                        backgroundColor: '#fef2f2',
                                        color: '#991b1b',
                                        border: '1px solid #fecaca',
                                        borderRadius: '0.25rem',
                                        cursor: 'pointer',
                                        fontSize: '0.7rem'
                                      }}
                                    >
                                      🗑️ Eliminar
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de detalle del día */}
      {selectedDay && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '2rem'
          }}
          onClick={() => {
            setSelectedDay(null);
            setDayDetail(null);
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: '2rem'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del modal */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                📅 Detalle del {new Date(selectedDay.fecha + 'T00:00:00').toLocaleDateString('es-AR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </h3>
              <button
                onClick={() => {
                  setSelectedDay(null);
                  setDayDetail(null);
                }}
                style={{
                  padding: '0.5rem',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {loadingDay ? (
              <div style={{ textAlign: 'center', padding: '3rem' }}>
                <RefreshCw size={32} className="animate-spin" style={{ margin: '0 auto 1rem' }} />
                <p style={{ color: '#6b7280' }}>Cargando detalle del día...</p>
              </div>
            ) : dayDetail ? (
              <div>
                {/* Resumen del día */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '1rem',
                  marginBottom: '2rem'
                }}>
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#eff6ff',
                    borderRadius: '0.375rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Horas trabajadas
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>
                      {formatHours(dayDetail.resumen.totalHoras)}
                    </div>
                  </div>

                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#f0fdf4',
                    borderRadius: '0.375rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Auditorías completadas
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
                      {dayDetail.resumen.totalAuditorias}
                    </div>
                  </div>

                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#fefce8',
                    borderRadius: '0.375rem',
                    textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                      Total ganado
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#059669' }}>
                      {formatMoney(dayDetail.resumen.totalGanado)}
                    </div>
                  </div>
                </div>

                {/* Sesión de trabajo */}
                {dayDetail.sesion && (
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '0.375rem',
                    marginBottom: '1.5rem'
                  }}>
                    <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                      ⏰ Sesión de trabajo
                    </h4>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                      Inicio: {new Date(dayDetail.sesion.loginTime).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                      {dayDetail.sesion.logoutTime && (
                        <> • Fin: {new Date(dayDetail.sesion.logoutTime).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</>
                      )}
                      {!dayDetail.sesion.logoutTime && <> • <span style={{ color: '#10b981', fontWeight: '600' }}>Sesión activa</span></>}
                    </div>
                  </div>
                )}

                {/* Lista de auditorías */}
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>
                    📋 Auditorías del día ({dayDetail.auditorias.length})
                  </h4>

                  {dayDetail.auditorias.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                      No se trabajó en auditorías este día
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {dayDetail.auditorias.map((auditoria) => (
                        <div
                          key={auditoria.id}
                          style={{
                            padding: '1rem',
                            backgroundColor: 'white',
                            border: '1px solid #e5e7eb',
                            borderRadius: '0.375rem'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                            <div>
                              <h5 style={{ fontSize: '0.875rem', fontWeight: '600', margin: '0 0 0.25rem 0' }}>
                                {auditoria.rol === 'supervisor' && '✏️ '}{auditoria.cliente} - {auditoria.sucursal}
                              </h5>
                              <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>
                                {auditoria.rol === 'supervisor' ? 'Corrección' : 'Estado'}: {auditoria.estado.replace('_', ' ')}
                              </p>
                            </div>

                            {/* Indicador de tiempo */}
                            <div style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '1rem',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              backgroundColor:
                                auditoria.indicador === 'ok' ? '#dcfce7' :
                                auditoria.indicador === 'rapido' ? '#dbeafe' :
                                '#fee2e2',
                              color:
                                auditoria.indicador === 'ok' ? '#15803d' :
                                auditoria.indicador === 'rapido' ? '#1e40af' :
                                '#991b1b'
                            }}>
                              {auditoria.indicador === 'ok' && '✓ Normal'}
                              {auditoria.indicador === 'rapido' && '⚡ Rápido'}
                              {auditoria.indicador === 'lento' && '⏱️ Lento'}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                            <div>
                              Tiempo: {formatHours(auditoria.tiempoTrabajado)} / {formatHours(auditoria.tiempoEstimado)} estimado
                            </div>
                            <div style={{ fontWeight: '600', color: '#059669' }}>
                              Comisión: {formatMoney(auditoria.comision)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
                <p>No hay datos disponibles para este día</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de reasignación */}
      {showReassignModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1001,
            padding: '2rem'
          }}
          onClick={() => setShowReassignModal(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              maxWidth: '500px',
              width: '100%',
              padding: '2rem'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: '0 0 0.5rem 0' }}>
              🔄 Reasignar auditoría
            </h3>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1.5rem 0' }}>
              {showReassignModal.titulo} — actualmente asignada a <strong>{showReassignModal.operadorActual}</strong>
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.375rem', color: '#374151' }}>
                Nuevo operador
              </label>
              <select
                value={reassignOperadorId}
                onChange={(e) => setReassignOperadorId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem'
                }}
              >
                <option value="">Seleccionar operador...</option>
                {empleados
                  .filter(emp => emp.usuario.id !== showReassignModal.operadorActualId && ['operador', 'supervisor'].includes(emp.usuario.role))
                  .map(emp => (
                    <option key={emp.usuario.id} value={emp.usuario.id}>
                      {emp.usuario.name} ({emp.usuario.role === 'operador' ? 'Operador' : 'Supervisor'})
                    </option>
                  ))
                }
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.375rem', color: '#374151' }}>
                Mensaje (opcional)
              </label>
              <textarea
                value={reassignMensaje}
                onChange={(e) => setReassignMensaje(e.target.value)}
                placeholder="Motivo de la reasignación..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowReassignModal(null)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleReassign}
                disabled={!reassignOperadorId}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: reassignOperadorId ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: reassignOperadorId ? 'pointer' : 'not-allowed',
                  fontSize: '0.875rem'
                }}
              >
                Reasignar
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default PersonalTracking;
